import { computed, defineComponent, PropType } from 'vue';
import { getStylesBinder } from '~mall/common/utils/styles';
import { timeFormat } from '~mall/common/utils/time';
import { getWord } from '~mall/common/utils/getWord';
import {
  CouponEquityDetail,
  Equity,
  EquityType,
} from '~mall/model/store/modules/ticket';
import styles from './index.module.less';

const cx = getStylesBinder(styles);

// 适配后的权益类型定义
type AdaptedEquity = Equity & {
  /** 权益描述列表 */
  descList: string[];
  /** 券左侧信息 */
  couponLeft: {
    /** 数字部分 */
    num: string;
    /** 文字部分 */
    word: string;
  };
  /** 图标地址 */
  iconUrl?: string;
  /** 阶梯 */
  promoInterval?: any;
};

// 类型守卫函数
const isCouponEquity = (item: AdaptedEquity): item is AdaptedEquity & CouponEquityDetail => (
  item.virtualType === EquityType.COUPON
);

export default defineComponent({
  name: 'CardDetail',
  props: {
    cardList: {
      type: Array as PropType<AdaptedEquity[]>,
      required: true,
    },
  },
  setup: (props) => {
    const getDisplayValue = (couponType: number, promoInterval: any, num: string | number) => {
      if ([7, 8].includes(couponType)) {
        if (promoInterval?.promoInfoList?.length) {
          const values = promoInterval.promoInfoList.map((item: any) => item.value);
          const minValue = Math.min(...values);
          const maxValue = Math.max(...values);
          return couponType === 7 ? `${minValue}-${maxValue}` : `${maxValue}-${minValue}`;
        }
        return '';
      }
      return num;
    };

    const adjustedFontSize = (value: string | number) => {
      const numLength = value.toString().length;
      if (numLength < 4) {
        return 25;
      }

      const scaleFactor = Math.max(15, 25 - (numLength - 3) * 3);
      return scaleFactor;
    };

    const isCardListMore = computed(() => props.cardList.length > 2);

    // 暂时不需要高亮
    // const highlightedText = (name: string) => {
    //   const truncatedName = name.length > 12 ? `${name.slice(0, 12)}...` : name;
    //   const regex = /\d+/g;
    //   const parts = truncatedName.split(regex);
    //   const numbers = truncatedName.match(regex);
    //   return parts
    //     .map((part, index) => {
    //       const number = numbers && numbers[index];
    //       return number
    //         ? `${part}<span
    //         style="
    //         font-family: DIN, sans-serif;
    //         font-weight: 700;
    //         font-size: 20px ;"
    //         class="highlighted-number">${number}</span>`
    //         : part;
    //     })
    //     .join('');
    // };

    const fillDesc = (item: AdaptedEquity): string => {
      const {
        virtualType,
        commodityDesc,
        effectDays,
        maxDiscountAmount,
        couponThreshold,
        ruleType,
        endTime,
        validDays,
      } = item;

      const couponType = isCouponEquity(item) ? item.couponType : undefined;
      const descList: string[] = [];

      if (effectDays) {
        const desc = commodityDesc?.replace(/[，,。]+$/, '').split(/[,，。]/);
        let totalLength = 0;
        const result = desc?.reduce((acc: string[], text) => {
          if (totalLength + text.length <= 12) {
            totalLength += text.length;
            acc.push(text);
          }
          return acc;
        }, []) || [];
        return result.join('') + (totalLength < (commodityDesc?.length || 0) ? '...' : '');
      }

      if (virtualType === 1 && couponType !== undefined) {
        switch (couponType) {
          case 1: {
            descList.push('使用金额无门槛');
            break;
          }
          default:
          // noop
        }
      }

      if (
        couponType === 2
        && typeof maxDiscountAmount === 'number'
        && maxDiscountAmount > 0
      ) {
        descList.push(`最高抵扣${maxDiscountAmount}元`);
      }

      if (couponType === 3 && typeof couponThreshold === 'number') {
        descList.push(`满${couponThreshold}元可用`);
      }

      if (ruleType === 1 && typeof validDays === 'number') {
        descList.push(`${validDays}天内有效`);
      }

      if (ruleType === 0 && typeof endTime === 'number') {
        descList.push(`有效期至${timeFormat(endTime)}`);
      }

      const result = descList.join(' | ');
      return result + (result.length < 28 ? '' : '...');
    };

    return () => (
      <div class={[cx('card-detail'), isCardListMore.value ? cx('list--more-wrap') : '']}>
        {props.cardList && props.cardList.length > 0 && (
          <div class={isCardListMore.value ? cx('list--more') : cx('list')}>
            {props.cardList.map((item, index) => (
              <div key={index} class={cx('list-item')}>
                {isCouponEquity(item) && [7, 8].includes(item.couponType) && (
                  <div>
                    <div class={cx('coupon-marker')}>随机立减</div>
                  </div>

                )}
                <div class={cx('list-item__left')}>
                  {item.propertyType === 5 ? (
                    <div class={cx('list-item__coupon-left')}>
                      <img src={item.iconUrl} alt="" class={cx('icon')} />
                    </div>
                  ) : <div class={cx('list-item__coupon-left')}>
                    <div class={cx('list-item__coupon-num')}>
                      <span
                          class={cx('coupon-value')}
                          style={{
                            fontSize: `${(() => {
                              const couponType = isCouponEquity(item) ? item.couponType : 0;
                              const displayValue = getDisplayValue(
                                couponType,
                                item.promoInterval,
                                item.couponLeft.num,
                              );
                              return adjustedFontSize(displayValue);
                            })()}px`,
                          }}
                      >
                        {(() => {
                          const couponType = isCouponEquity(item) ? item.couponType : 0;
                          return getDisplayValue(
                            couponType,
                            item.promoInterval,
                            item.couponLeft.num,
                          );
                        })()}
                      </span>
                    </div>
                    <div class={cx('list-item__coupon-word')}>
                      {getWord(item.couponLeft.word, item.propertyType, cx('icon'))}
                    </div>

                  </div>}

                  <div class={cx('list-item__title-wrap')}>
                    <div class={cx('list-item__title')}>
                      {item.virtualName}
                    </div>
                    {item.descList && item.descList.length ? (
                      <div class={cx('list-item__desc')}>
                        {item.descList.length ? fillDesc(item) : ''}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div class={cx('list-item__right')}>
                  <span class={cx('list-item__right-x')}>X</span>
                  <span class={cx('list-item__right-num')}>{item.virtualNum}</span>
                  <span>张</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
});
